import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sparkles, Upload, Shirt, Wand2, Star, Sun, Moon } from "lucide-react";
import TryNowModal from "../components/TryNowModal";
import { useSEO } from "../hooks/useSEO";
import { useAuth } from "../hooks/useAuth";
import "../App.css";

function StepCard({ step, emoji, title, desc, delay, isLast }) {
	const ref = useRef(null);
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		const el = ref.current;
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) setVisible(true);
			},
			{ threshold: 0.2 },
		);
		if (el) observer.observe(el);
		return () => observer.disconnect();
	}, []);

	return (
		<div
			ref={ref}
			className={`step-card ${visible ? "step-card--visible" : ""}`}
			style={{ transitionDelay: `${delay}ms` }}
		>
			<div className="step-number">{step}</div>
			<div className="step-emoji">{emoji}</div>
			<h3 className="step-title">{title}</h3>
			<p className="step-desc">{desc}</p>
			{!isLast && <div className="step-arrow">↓</div>}
		</div>
	);
}

function getInitialTheme() {
	if (typeof window === "undefined") return "light";
	const stored = localStorage.getItem("puton-theme");
	if (stored === "light" || stored === "dark") return stored;
	return "dark";
}

export default function Home() {
	const navigate = useNavigate();
	const location = useLocation();
	const loggedIn = useAuth();

	const [showTryModal, setShowTryModal] = useState(false);
	const [heroVisible, setHeroVisible] = useState(false);
	const [theme, setTheme] = useState(getInitialTheme);

	/* Auto-open modal only when /try-on sent user here and they are not logged in */
	useEffect(() => {
		if (location.state?.requireLogin && !loggedIn) {
			setShowTryModal(true);
			window.history.replaceState({}, "", location.pathname);
		}
	}, [location.state, location.pathname, loggedIn]);

	useSEO({
		title: "Virtual Try On with AI | PutOn AI Virtual Fitting Room",
		description:
			"PutOn AI is an AI virtual try on platform. Try on clothes online with your photo, preview fit in seconds, and shop with confidence using our virtual fitting room.",
		canonical: "https://putonai.com/",
		ogImage: "https://putonai.com/logo.png",
	});

	useEffect(() => {
		document.documentElement.setAttribute("data-theme", theme);
		localStorage.setItem("puton-theme", theme);
	}, [theme]);

	useEffect(() => {
		const t = setTimeout(() => setHeroVisible(true), 100);
		return () => clearTimeout(t);
	}, []);

	const founders = [
		{
			name: "Sagar Sharma",
			role: "Co-Founder | Software Developer | AI First Engineer",
			education:
				"B.Tech in Computer Engineering, J.C. Bose University of Science and Technology, YMCA",
			bio: "Brings 4+ years of experience in AI integration, project management, and e-commerce. Focused on building scalable backend systems and intelligent solutions that drive business growth.",
			image: `${process.env.PUBLIC_URL}/team/sagar-sharma.png`,
			imageAlt: "Sagar Sharma — professional portrait",
		},
		{
			name: "Ravinder",
			role: "Co-Founder | Robotics Engineer | AI Engineer",
			education:
				"B.Tech in Computer Engineering, J.C. Bose University of Science and Technology, YMCA",
			bio: "Has 4+ years of experience in IoT, robotics, embedded systems, and AI. Specializes in integrating hardware with smart software solutions.",
			image: `${process.env.PUBLIC_URL}/team/ravinder.png`,
			imageAlt: "Ravinder — professional portrait",
		},
		{
			name: "Shahid",
			role: "Co-Founder | Software Developer | Product Manager",
			education:
				"B.Tech in Information Technology, J.C. Bose University of Science and Technology, YMCA",
			bio: "Brings 4+ years of experience in project management, e-commerce, and supply chain. Ensures smooth execution and operational excellence.",
			image: `${process.env.PUBLIC_URL}/team/sahid.png`,
			imageAlt: "Sahid — professional portrait",
		},
	];

	const steps = [
		{
			step: "01",
			emoji: "📸",
			title: "Upload Your Photo",
			desc: "Take a full-body photo or pick one from your gallery. Any background works — our AI segments you automatically.",
		},
		{
			step: "02",
			emoji: "👗",
			title: "Pick a Garment",
			desc: "Upload a product image or paste a link from any fashion brand. We extract the garment for you.",
		},
		{
			step: "03",
			emoji: "🤖",
			title: "AI Does the Magic",
			desc: "Our model analyses your body shape, garment drape, lighting and color to create a photorealistic result.",
		},
		{
			step: "04",
			emoji: "✨",
			title: "See Your Look",
			desc: "View, download or share the result. Love it? Tap the buy link and you're done.",
		},
	];

	return (
		<div className="app">
			{showTryModal && <TryNowModal onClose={() => setShowTryModal(false)} />}

			<nav className="nav" aria-label="Main navigation">
				<Link
					to="/"
					className="nav-logo"
					aria-label="PutOn AI — go to homepage"
				>
					<img
						src={`${process.env.PUBLIC_URL}/logo.png`}
						alt="PutOn AI logo"
						className="nav-logo-img"
						width={168}
						height={168}
					/>
				</Link>
				<div className="nav-actions">
					<a
						href="#mission"
						className="nav-link-btn"
						aria-label="Go to Our Mission section"
					>
						Our Mission
					</a>
					<button
						type="button"
						className="nav-cta"
						onClick={() =>
							loggedIn ? navigate("/try-on") : setShowTryModal(true)
						}
						aria-label="Try PutOn AI virtual try-on now"
					>
						Try Now
					</button>
				</div>
			</nav>

			<section className="hero" aria-label="Hero — AI virtual try-on overview">
				<div
					className={`hero-content ${heroVisible ? "hero-content--visible" : ""}`}
				>
					<span className="badge">
						<Sparkles size={13} /> AI-Powered Virtual Try-On
					</span>
					<h1 className="hero-title">
						Virtual Try On
						<br />
						<span className="gradient-text">with AI Before You Buy</span>
					</h1>
					<p className="hero-subtitle">
						Try on clothes online with your photo. PutOn AI gives you a
						photorealistic preview in seconds so you can shop smarter and return
						less.
					</p>
					<div className="hero-metrics" aria-label="Product highlights">
						<span>3s Try-On</span>
						<span>AI Fit Preview</span>
						<span>Lower Returns</span>
					</div>
					<button
						type="button"
						className="hero-btn"
						onClick={() =>
							loggedIn ? navigate("/try-on") : setShowTryModal(true)
						}
						aria-label="Start your free AI virtual try-on"
					>
						Try Now
					</button>
				</div>

				<div
					className={`mockup ${heroVisible ? "mockup--visible" : ""}`}
					role="img"
					aria-label="Animated demo: upload your photo plus a garment, AI produces your virtual try-on result"
				>
					<div className="mockup-card">
						<div className="mockup-header">
							<span className="dot" />
							<span className="dot" />
							<span className="dot" />
						</div>
						<div className="mockup-body">
							<div className="mockup-slot">
								<Upload size={28} className="mockup-icon" />
								<span>Your Photo</span>
							</div>
							<div className="mockup-plus">+</div>
							<div className="mockup-slot">
								<Shirt size={28} className="mockup-icon" />
								<span>Garment</span>
							</div>
							<div className="mockup-equals">=</div>
							<div className="mockup-slot mockup-slot--result">
								<Wand2 size={28} className="mockup-icon mockup-icon--glow" />
								<span>Your Look</span>
								<span className="mockup-badge">AI</span>
							</div>
						</div>
						<div className="mockup-footer">
							<div className="progress-bar">
								<div className="progress-fill" />
							</div>
							<span className="processing-text">Processing with AI…</span>
						</div>
					</div>
					<div className="float-tag float-tag--1">⚡ 3-sec result</div>
					<div className="float-tag float-tag--2">🎯 98% accuracy</div>
				</div>

				<div className="blob blob--1" />
				<div className="blob blob--2" />
			</section>

			<section
				className="how-section"
				id="how-it-works"
				aria-labelledby="how-it-works-heading"
			>
				<div className="section-header">
					<h2 id="how-it-works-heading" className="section-title">
						How PutOn AI Works
					</h2>
					<p className="section-subtitle">
						Four simple steps from photo to virtual fitting room.
					</p>
				</div>

				<div className="steps-grid">
					{steps.map((s, i) => (
						<StepCard
							key={s.step}
							{...s}
							delay={i * 120}
							isLast={i === steps.length - 1}
						/>
					))}
				</div>
			</section>

			<section
				className="mission-section"
				id="mission"
				aria-labelledby="mission-heading"
			>
				<div className="section-header">
					<h2 id="mission-heading" className="section-title">
						Our Mission
					</h2>
				</div>
				<div className="mission-card">
					<p>
						At Puton, we are redefining how people shop for clothes by bringing
						confidence into every purchase.
					</p>
					<p>
						We believe buying fashion should not be based on guesswork,
						assumptions, or uncertainty. Instead, it should be simple, accurate,
						and personal.
					</p>
					<p>
						By combining artificial intelligence with everyday shopping, we
						enable people to see how clothes truly look on them before making a
						decision.
					</p>
					<p>
						Our mission is to eliminate doubt, reduce returns, and make online
						fashion more reliable, intuitive, and accessible for everyone.
					</p>
					<p>
						We are building a future where trying clothes is no longer limited
						to physical stores, but available instantly, anywhere.
					</p>
				</div>
			</section>

			<section
				className="founders-section founders-section--compact"
				id="founders"
				aria-labelledby="founders-heading"
			>
				<div className="section-header section-header--compact">
					<h2 id="founders-heading" className="section-title">
						Meet Our Founders
					</h2>
				</div>

				<div className="founders-grid founders-grid--compact">
					{founders.map((f) => (
						<article
							key={f.name}
							className="founder-card founder-card--compact"
						>
							<div className="founder-card__image-wrap">
								<img
									src={f.image}
									alt={f.imageAlt || f.name}
									className="founder-card__image"
									loading="lazy"
									decoding="async"
								/>
							</div>
							<h3 className="founder-card__name">{f.name}</h3>
							<p className="founder-card__role">{f.role}</p>
						</article>
					))}
				</div>
			</section>

			<section
				className="download-section"
				id="download"
				aria-labelledby="download-heading"
			>
				<div className="download-blob" />
				<div className="download-content">
					<Star size={32} className="download-star" />
					<h2 id="download-heading" className="download-title">
						Ready to try PutOn AI?
					</h2>
					<p className="download-subtitle">
						Open the demo flow to see how photo, garment, and AI come together —
						mobile apps coming soon.
					</p>
					<div className="store-buttons store-buttons--single">
						<button
							type="button"
							className="store-btn store-btn--primary"
							onClick={() =>
								loggedIn ? navigate("/try-on") : setShowTryModal(true)
							}
							aria-label="Try PutOn AI virtual try-on demo"
						>
							Try Now
						</button>
					</div>
				</div>
			</section>

			<footer className="footer" role="contentinfo">
				<Link
					to="/"
					className="footer-logo"
					aria-label="PutOn AI — go to homepage"
				>
					<img
						src={`${process.env.PUBLIC_URL}/logo.png`}
						alt="PutOn AI logo"
						className="footer-logo-img"
						width={28}
						height={28}
					/>
					PutOn<span>AI</span>
				</Link>
				<p className="footer-copy">
					© 2026 PutOn AI · Built with love for fashion lovers
				</p>
				<button
					type="button"
					className="footer-theme-chip"
					onClick={() =>
						setTheme((prev) => (prev === "dark" ? "light" : "dark"))
					}
					aria-label={
						theme === "dark" ? "Switch to light mode" : "Switch to dark mode"
					}
				>
					{theme === "dark" ? (
						<>
							<Sun size={15} strokeWidth={2} />
							Light Mode
						</>
					) : (
						<>
							<Moon size={15} strokeWidth={2} />
							Dark Mode
						</>
					)}
				</button>
			</footer>
		</div>
	);
}
